import { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Download, Share2, Sparkles, Trophy, Users, BookOpen, Video, Award, Calendar } from 'lucide-react';
import { startOfYear, endOfYear, format } from 'date-fns';
import { toast } from 'sonner';

interface YearlyStats {
  totalUsers: number;
  totalDistricts: number;
  totalCourses: number;
  totalEnrollments: number;
  completedCourses: number;
  totalMeetings: number;
  totalVideos: number;
  totalBadges: number;
  totalSurveys: number;
  topDistrict: { name: string; engagement: number } | null;
  topCourse: { title: string; enrollments: number } | null;
  growthRate: number;
}

export default function YearlyWrapUp() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<YearlyStats | null>(null);
  const currentYear = new Date().getFullYear();

  useEffect(() => {
    if (user?.email === 'matt.monjan@infusedu.com' || user?.email === 'matt.monjan@infusesdu.com') {
      loadYearlyStats();
    }
  }, [user]);

  const loadYearlyStats = async () => {
    setLoading(true);
    try {
      const yearStart = startOfYear(new Date());
      const yearEnd = endOfYear(new Date());
      const lastYearStart = startOfYear(new Date(currentYear - 1, 0, 1));
      const lastYearEnd = endOfYear(new Date(currentYear - 1, 11, 31));

      // Current year data
      const [
        { data: profiles },
        { data: districts },
        { data: courses },
        { data: enrollments },
        { data: meetings },
        { data: videos },
        { data: badges },
        { data: surveys },
        { data: lastYearEnrollments },
      ] = await Promise.all([
        supabase.from('profiles').select('id, district_id'),
        supabase.from('districts').select('id, name'),
        supabase.from('courses').select('id, title').gte('created_at', yearStart.toISOString()).lte('created_at', yearEnd.toISOString()),
        supabase.from('enrollments').select('id, status, course_id').gte('enrollment_date', yearStart.toISOString()).lte('enrollment_date', yearEnd.toISOString()),
        supabase.from('meetings').select('id').gte('created_at', yearStart.toISOString()).lte('created_at', yearEnd.toISOString()),
        supabase.from('videos').select('id').gte('created_at', yearStart.toISOString()).lte('created_at', yearEnd.toISOString()),
        supabase.from('user_badges').select('id').gte('awarded_at', yearStart.toISOString()).lte('awarded_at', yearEnd.toISOString()),
        supabase.from('surveys').select('id').gte('created_at', yearStart.toISOString()).lte('created_at', yearEnd.toISOString()),
        supabase.from('enrollments').select('id').gte('enrollment_date', lastYearStart.toISOString()).lte('enrollment_date', lastYearEnd.toISOString()),
      ]);

      // Calculate top district by engagement
      let topDistrict = null;
      if (districts && districts.length > 0) {
        const districtEngagement = await Promise.all(
          districts.map(async (district) => {
            const districtUsers = profiles?.filter(p => p.district_id === district.id) || [];
            const { data: activity } = await supabase
              .from('activity_logs')
              .select('user_id')
              .in('user_id', districtUsers.map(u => u.id))
              .gte('created_at', new Date(currentYear, 0, 1).toISOString());
            
            const activeUsers = new Set(activity?.map(a => a.user_id)).size || 0;
            
            return {
              name: district.name,
              engagement: districtUsers.length > 0 ? Math.round((activeUsers / districtUsers.length) * 100) : 0,
            };
          })
        );
        topDistrict = districtEngagement.sort((a, b) => b.engagement - a.engagement)[0] || null;
      }

      // Calculate top course
      let topCourse = null;
      if (courses && courses.length > 0) {
        const courseEnrollments = courses.map(course => ({
          title: course.title,
          enrollments: enrollments?.filter(e => e.course_id === course.id).length || 0,
        }));
        topCourse = courseEnrollments.sort((a, b) => b.enrollments - a.enrollments)[0] || null;
      }

      // Calculate growth rate
      const currentEnrollments = enrollments?.length || 0;
      const lastEnrollments = lastYearEnrollments?.length || 1;
      const growthRate = Math.round(((currentEnrollments - lastEnrollments) / lastEnrollments) * 100);

      setStats({
        totalUsers: profiles?.length || 0,
        totalDistricts: districts?.length || 0,
        totalCourses: courses?.length || 0,
        totalEnrollments: enrollments?.length || 0,
        completedCourses: enrollments?.filter(e => e.status === 'completed').length || 0,
        totalMeetings: meetings?.length || 0,
        totalVideos: videos?.length || 0,
        totalBadges: badges?.length || 0,
        totalSurveys: surveys?.length || 0,
        topDistrict,
        topCourse,
        growthRate,
      });
    } catch (error) {
      console.error('Error loading yearly stats:', error);
      toast.error('Failed to load yearly statistics');
    } finally {
      setLoading(false);
    }
  };

  const exportWrapUp = () => {
    toast.success('Wrap-up report exported!');
  };

  if (user?.email !== 'matt.monjan@infusedu.com' && user?.email !== 'matt.monjan@infusesdu.com') {
    return (
      <div className="container mx-auto py-8">
        <Card>
          <CardContent className="py-12 text-center">
            <h2 className="text-2xl font-bold mb-2">Access Denied</h2>
            <p className="text-muted-foreground">This page is only accessible to authorized administrators.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary/20 via-accent/20 to-primary/20 flex items-center justify-center">
        <Card className="w-96">
          <CardContent className="py-12 text-center">
            <Sparkles className="h-12 w-12 mx-auto mb-4 animate-pulse text-primary" />
            <p className="text-muted-foreground">Loading your {currentYear} Wrap Up...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/20 via-accent/20 to-primary/20 py-12">
      <div className="container mx-auto max-w-4xl">
        <div className="flex items-center justify-between mb-8 animate-fade-in">
          <Button variant="ghost" onClick={() => window.history.back()}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={exportWrapUp} className="hover-scale">
              <Download className="mr-2 h-4 w-4" />
              Export
            </Button>
            <Button variant="outline" className="hover-scale">
              <Share2 className="mr-2 h-4 w-4" />
              Share
            </Button>
          </div>
        </div>

        <div className="space-y-8">
          {/* Hero Section */}
          <Card className="overflow-hidden bg-gradient-to-br from-primary to-primary/80 text-primary-foreground animate-scale-in">
            <CardContent className="p-12 text-center">
              <Sparkles className="h-16 w-16 mx-auto mb-6 animate-pulse" />
              <h1 className="text-5xl font-bold mb-4">Edugogy {currentYear}</h1>
              <p className="text-2xl font-semibold mb-2">Your Year in Professional Development</p>
              <p className="text-lg opacity-90">A celebration of growth, learning, and impact</p>
            </CardContent>
          </Card>

          {/* Key Metrics */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white hover-scale animate-fade-in" style={{ animationDelay: '0.1s' }}>
              <CardContent className="p-6 text-center">
                <Users className="h-8 w-8 mx-auto mb-2" />
                <div className="text-3xl font-bold">{stats?.totalUsers}</div>
                <div className="text-sm opacity-90">Educators</div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white hover-scale animate-fade-in" style={{ animationDelay: '0.2s' }}>
              <CardContent className="p-6 text-center">
                <BookOpen className="h-8 w-8 mx-auto mb-2" />
                <div className="text-3xl font-bold">{stats?.totalCourses}</div>
                <div className="text-sm opacity-90">Courses Created</div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white hover-scale animate-fade-in" style={{ animationDelay: '0.3s' }}>
              <CardContent className="p-6 text-center">
                <Award className="h-8 w-8 mx-auto mb-2" />
                <div className="text-3xl font-bold">{stats?.completedCourses}</div>
                <div className="text-sm opacity-90">Completions</div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-orange-500 to-orange-600 text-white hover-scale animate-fade-in" style={{ animationDelay: '0.4s' }}>
              <CardContent className="p-6 text-center">
                <Trophy className="h-8 w-8 mx-auto mb-2" />
                <div className="text-3xl font-bold">{stats?.totalBadges}</div>
                <div className="text-sm opacity-90">Badges Awarded</div>
              </CardContent>
            </Card>
          </div>

          {/* Highlights */}
          <Card className="animate-fade-in" style={{ animationDelay: '0.5s' }}>
            <CardContent className="p-8">
              <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
                <Sparkles className="h-6 w-6 text-primary" />
                {currentYear} Highlights
              </h2>
              <div className="space-y-6">
                <div className="flex items-start gap-4 hover-scale transition-all">
                  <div className="bg-primary/10 p-3 rounded-lg">
                    <Users className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">Most Engaged District</h3>
                    <p className="text-muted-foreground">
                      {stats?.topDistrict?.name} led the way with {stats?.topDistrict?.engagement}% engagement
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4 hover-scale transition-all">
                  <div className="bg-primary/10 p-3 rounded-lg">
                    <BookOpen className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">Most Popular Course</h3>
                    <p className="text-muted-foreground">
                      "{stats?.topCourse?.title}" with {stats?.topCourse?.enrollments} enrollments
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4 hover-scale transition-all">
                  <div className="bg-primary/10 p-3 rounded-lg">
                    <Calendar className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">Growth This Year</h3>
                    <p className="text-muted-foreground">
                      {stats?.growthRate}% increase in enrollments compared to {currentYear - 1}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4 hover-scale transition-all">
                  <div className="bg-primary/10 p-3 rounded-lg">
                    <Video className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">Content Creation</h3>
                    <p className="text-muted-foreground">
                      {stats?.totalVideos} videos uploaded and {stats?.totalMeetings} mentoring meetings conducted
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Additional Stats */}
          <Card className="animate-fade-in" style={{ animationDelay: '0.6s' }}>
            <CardContent className="p-8">
              <h2 className="text-2xl font-bold mb-6">By the Numbers</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="text-center hover-scale transition-all">
                  <div className="text-4xl font-bold text-primary mb-2">{stats?.totalEnrollments}</div>
                  <div className="text-sm text-muted-foreground">Total Enrollments</div>
                </div>
                <div className="text-center hover-scale transition-all">
                  <div className="text-4xl font-bold text-primary mb-2">{stats?.totalSurveys}</div>
                  <div className="text-sm text-muted-foreground">Surveys Created</div>
                </div>
                <div className="text-center hover-scale transition-all">
                  <div className="text-4xl font-bold text-primary mb-2">{stats?.totalDistricts}</div>
                  <div className="text-sm text-muted-foreground">Active Districts</div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Thank You Message */}
          <Card className="bg-gradient-to-br from-accent/30 to-accent/20 animate-fade-in" style={{ animationDelay: '0.7s' }}>
            <CardContent className="p-12 text-center">
              <h2 className="text-3xl font-bold mb-4">Thank You!</h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Here's to another year of empowering educators, fostering growth, and making a lasting impact on professional development. 
                Together, we're building the future of education.
              </p>
              <div className="mt-8">
                <p className="text-sm text-muted-foreground">
                  Generated on {format(new Date(), 'PPP')}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
